/// <amd-module name="Artifacts/FeedSettings/redux/Reducer" />

import {
  FeedSettingsActions,
  FeedSettingsActionTypes,
  IAddOrEditViewCompletedPayload,
  IAddUpstreamsPayload,
  IDeleteViewCompletedPayload,
  ILoadFeedViewsCompletedPayload,
  ILoadPermissionsCompletedPayload,
  ILoadServiceConnectionsCompleted,
  ILoadUpstreamFeedsCompleted,
  ILoadViewsPermissionsCompletedPayload,
  IMakeViewDefaultCompletedPayload,
  ISettingsInitLoadCompletedPayload,
  IUpdatePermissionsCompletedPayload,
  IUpstreamsPayload,
} from "Artifacts/FeedSettings/redux/Actions";
import { IFeedSettingsHubState } from "Artifacts/FeedSettings/redux/Contracts";
import {
  LoadingComponentState,
  ReadyComponentState,
} from "Artifacts/Root/helpers/ComponentStateHelpers";
import { syncComponentState } from "Artifacts/Root/redux";
import produce from "immer";

const initialState = (): IFeedSettingsHubState => ({
  componentState: LoadingComponentState(),
  saveGeneralSettingsComponentState: ReadyComponentState(),
  deleteFeedComponentState: ReadyComponentState(),
  permanentDeleteFeedComponentState: ReadyComponentState(),
  permissionsComponentState: LoadingComponentState(),
  viewsPermissionsComponentState: LoadingComponentState(),
  saveUpstreamsComponentState: ReadyComponentState(),
  addUpstreamComponentState: ReadyComponentState(),
  addPermissionComponentState: ReadyComponentState(),
  deletePermissionComponentState: ReadyComponentState(),
  sameCollectionFeedListComponentState: LoadingComponentState(),
  updateViewPermissionsComponentState: ReadyComponentState(),
  serviceConnectionsComponentState: LoadingComponentState(),
  deleteFeedViewComponentState: ReadyComponentState(),
  isUpstreamsDirty: false,
});

export const feedSettingsHubReducer = produce(
  (draft: IFeedSettingsHubState, action: FeedSettingsActions) => {
    syncComponentState(draft, "componentState", action, {
      ready: FeedSettingsActionTypes.InitLoadCompleted,
      loading: FeedSettingsActionTypes.InitLoadStarted,
      error: FeedSettingsActionTypes.InitLoadFailed,
    });
    syncComponentState(draft, "saveGeneralSettingsComponentState", action, {
      ready: FeedSettingsActionTypes.SaveGeneralFeedSettingsCompleted,
      loading: FeedSettingsActionTypes.SaveGeneralFeedSettings,
      error: FeedSettingsActionTypes.SaveGeneralFeedSettingsFailed,
    });
    syncComponentState(draft, "deleteFeedComponentState", action, {
      ready: FeedSettingsActionTypes.DeleteFeedCompleted,
      loading: FeedSettingsActionTypes.DeleteFeed,
      error: FeedSettingsActionTypes.DeleteFeedFailed,
    });
    syncComponentState(draft, "permanentDeleteFeedComponentState", action, {
      ready: FeedSettingsActionTypes.PermanentDeleteFeedCompleted,
      loading: FeedSettingsActionTypes.PermanentDeleteFeed,
      error: FeedSettingsActionTypes.PermanentDeleteFeedFailed,
    });
    syncComponentState(draft, "permissionsComponentState", action, {
      ready: FeedSettingsActionTypes.LoadPermissionsCompleted,
      loading: FeedSettingsActionTypes.LoadPermissions,
      error: FeedSettingsActionTypes.LoadPermissionsFailed,
    });
    syncComponentState(draft, "viewsPermissionsComponentState", action, {
      ready: FeedSettingsActionTypes.LoadViewsPermissionsCompleted,
      loading: FeedSettingsActionTypes.LoadViewsPermissions,
      error: FeedSettingsActionTypes.LoadViewsPermissionsFailed,
    });
    syncComponentState(draft, "saveUpstreamsComponentState", action, {
      ready: FeedSettingsActionTypes.SaveUpstreamConfigurationCompleted,
      loading: FeedSettingsActionTypes.SaveUpstreamConfiguration,
      error: FeedSettingsActionTypes.SaveUpstreamConfigurationFailed,
    });
    syncComponentState(draft, "sameCollectionFeedListComponentState", action, {
      ready: FeedSettingsActionTypes.LoadFeedsCompleted,
      loading: FeedSettingsActionTypes.LoadFeeds,
      error: FeedSettingsActionTypes.LoadFeedsFailed,
    });
    syncComponentState(draft, "serviceConnectionsComponentState", action, {
      ready: FeedSettingsActionTypes.LoadServiceConnectionsCompleted,
      loading: FeedSettingsActionTypes.LoadServiceConnections,
      error: FeedSettingsActionTypes.LoadServiceConnectionsFailed,
    });
    syncComponentState(draft, "addPermissionComponentState", action, {
      ready: FeedSettingsActionTypes.AddPermissionCompleted,
      loading: FeedSettingsActionTypes.AddPermission,
      error: FeedSettingsActionTypes.AddPermissionFailed,
    });
    syncComponentState(draft, "deletePermissionComponentState", action, {
      ready: FeedSettingsActionTypes.DeletePermissionCompleted,
      loading: FeedSettingsActionTypes.DeletePermission,
      error: FeedSettingsActionTypes.DeletePermissionFailed,
    });
    syncComponentState(draft, "updateViewPermissionsComponentState", action, {
      ready: FeedSettingsActionTypes.AddOrEditViewCompleted,
      loading: FeedSettingsActionTypes.AddOrEditView,
      error: FeedSettingsActionTypes.AddOrEditViewFailed,
    });
    syncComponentState(draft, "deleteFeedViewComponentState", action, {
      ready: FeedSettingsActionTypes.DeleteViewCompleted,
      loading: FeedSettingsActionTypes.DeleteView,
      error: FeedSettingsActionTypes.DeleteViewFailed,
    });
    switch (action.type) {
      case FeedSettingsActionTypes.InitLoadCompleted:
        draft.settings = (action.payload as ISettingsInitLoadCompletedPayload).settings;
        break;
      case FeedSettingsActionTypes.LoadPermissionsCompleted:
        draft.permissions = (action.payload as ILoadPermissionsCompletedPayload).permissions;
        break;
      case FeedSettingsActionTypes.AddPermissionCompleted:
      case FeedSettingsActionTypes.DeletePermissionCompleted:
        draft.permissions = (action.payload as IUpdatePermissionsCompletedPayload).permissions;
        break;
      case FeedSettingsActionTypes.LoadViewsPermissionsCompleted:
        draft.viewPermissionMap = (action.payload as ILoadViewsPermissionsCompletedPayload).permissionMap;
        break;
      case FeedSettingsActionTypes.UpdateUpstreamList:
        if (draft.settings) {
          draft.settings.upstreamSources = (action.payload as IUpstreamsPayload).upstreams;
          draft.isUpstreamsDirty = true;
        }
        break;
      case FeedSettingsActionTypes.LoadFeedsCompleted:
        draft.feeds = (action.payload as ILoadUpstreamFeedsCompleted).feeds;
        break;
      case FeedSettingsActionTypes.LoadFeedViewsCompleted:
        draft.feedViews = (action.payload as ILoadFeedViewsCompletedPayload).views;
        break;
      case FeedSettingsActionTypes.LoadServiceConnectionsCompleted:
        draft.serviceEndpoints = (action.payload as ILoadServiceConnectionsCompleted).serviceConnections;
        break;
      case FeedSettingsActionTypes.AddUpstreams:
        if (draft.settings) {
          draft.settings.upstreamSources.push(
            ...(action.payload as IAddUpstreamsPayload).upstreams,
          );
          draft.isUpstreamsDirty = true;
        }
        break;
      case FeedSettingsActionTypes.MakeViewDefaultCompleted:
        if (draft.settings) {
          draft.settings.defaultViewId = (action.payload as IMakeViewDefaultCompletedPayload).defaultViewId;
        }
        break;
      case FeedSettingsActionTypes.AddOrEditViewCompleted:
        if (draft.settings) {
          const {
            newModifiedView,
            defaultFeedViewId,
            permissions,
          } = action.payload as IAddOrEditViewCompletedPayload;

          // Add or update view
          const views = draft.settings.views.filter((x) => x.id !== newModifiedView.id);
          views.push(newModifiedView);
          // Sort views alphabetically so they match server
          const sortedViews = views.sort((a, b) => a.name.localeCompare(b.name, navigator.language));
          draft.settings.views = sortedViews;

          if (defaultFeedViewId) {
            draft.settings.defaultViewId = defaultFeedViewId;
          }

          if (draft.viewPermissionMap) {
            draft.viewPermissionMap[newModifiedView.id] = permissions;
          }
        }
        break;
      case FeedSettingsActionTypes.DeleteViewCompleted:
        if (draft.settings) {
          draft.settings.views = draft.settings.views.filter(
            (x) => x.id !== (action.payload as IDeleteViewCompletedPayload).view.id,
          );
        }
        break;
      case FeedSettingsActionTypes.SaveUpstreamConfigurationCompleted:
        draft.isUpstreamsDirty = false;
        break;
      case FeedSettingsActionTypes.SaveUpstreamConfigurationComponentReset:
        draft.saveUpstreamsComponentState = ReadyComponentState();
        break;
    }
    return draft;
  },
  initialState(),
);
